import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { apiClient } from '@/api/client'
import { useDeleteGameAsAdmin } from '@/hooks/useGames'
import { useRegenerateAISummary } from '@/hooks/useAI'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { getTeamThatScoredPoints, getEventLabel } from '@/utils/goalUtils'
import { 
  ArrowLeft, 
  Calendar, 
  Trophy, 
  Users, 
  Target,
  Zap,
  Clock,
  Trash2,
  User,
  RefreshCw
} from 'lucide-react'

interface GameParticipant {
  id: string
  user_id: string
  team: 'red' | 'blue'
  role: 'defense' | 'attack' | 'full'
  user: {
    id: string
    display_name: string
    profile_picture_url?: string
  }
}

interface Goal {
  id: string
  scored_by_team: 'red' | 'blue'
  goal_type: string
  shooter_role: string
  points_awarded: number
  touched_by_defender: boolean
  goal_time: string | null
  created_at: string
  user: {
    id: string
    display_name: string
    profile_picture_url?: string
  } | null
}

interface Game {
  id: string
  date: string
  game_type: '1v1' | '2v2'
  target_score: number
  final_red_score: number
  final_blue_score: number
  winner?: 'red' | 'blue'
  ai_summary?: string
  quick_entry: boolean
  participants: GameParticipant[]
  goals?: Goal[]
}

export function GameDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { getAccessTokenSilently } = useAuth0()
  const deleteGame = useDeleteGameAsAdmin()
  const regenerateAI = useRegenerateAISummary()

  const { data: game, isLoading } = useQuery({
    queryKey: ['game', id],
    queryFn: async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.get(`/games/${id}`)
      return response.data
    },
    enabled: !!id,
  })

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatTime = (dateString: string | null | undefined, fallbackDate?: string) => {
    // Use goal_time if available, otherwise fall back to created_at or game date
    const timeToUse = dateString || fallbackDate
    if (!timeToUse) return 'N/A'
    
    const date = new Date(timeToUse)
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const getGoalTypeLabel = (goalType: string) => {
    const labels: Record<string, string> = {
      'attack_shot': 'Attack Shot',
      'midfield_shot': 'Midfield Shot',
      'defense_shot': 'Defense Shot',
      'goalkeeper_shot': 'Goalkeeper Shot',
      'own_goal_attack': 'Own Goal (Attack)',
      'own_goal_midfield': 'Own Goal (Midfield)',
      'own_goal_defense': 'Own Goal (Defense)',
      'own_goal_goalkeeper': 'Own Goal (Goalkeeper)',
      'monsterblock': 'Monsterblock',
      'monsterblock_defense': 'Monsterblock (Defense)',
      'monsterblock_goalkeeper': 'Monsterblock (Goalkeeper)',
      'gamelle': 'Gamelle',
      'gamelle_attack': 'Gamelle (Attack)',
      'gamelle_midfield': 'Gamelle (Midfield)',
      'gamelle_defense': 'Gamelle (Defense)',
      'gamelle_goalkeeper': 'Gamelle (Goalkeeper)'
    }
    return labels[goalType] || goalType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      'attack': 'Attack',
      'midfield': 'Midfield',
      'defense': 'Defense',
      'goalkeeper': 'Goalkeeper',
      'full': 'Full Side'
    }
    return labels[role] || role
  }

  const getParticipantsByTeam = (participants: GameParticipant[], team: 'red' | 'blue') => {
    return participants.filter(p => p.team === team)
  }

  const getWinner = (game: Game) => {
    if (game.winner) {
      return game.winner
    }
    return 'tie'
  }

  const handleDeleteGame = async () => {
    if (window.confirm('Are you sure you want to delete this game? This action cannot be undone.')) {
      try {
        await deleteGame.mutateAsync(id!)
        navigate('/games')
      } catch (error) {
        console.error('Failed to delete game:', error)
        alert('Failed to delete game. You may not have permission.')
      }
    }
  }

  const handleRegenerateAI = async () => {
    try {
      await regenerateAI.mutateAsync(id!)
    } catch (error) {
      console.error('Failed to regenerate AI summary:', error)
      alert('Failed to generate AI summary. The AI service may be unavailable.')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!game) {
    return (
      <div className="space-y-6">
        <Link
          to="/games"
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Games
        </Link>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900">Game not found</h3>
        </div>
      </div>
    )
  }

  const winner = getWinner(game)
  const redParticipants = getParticipantsByTeam(game.participants, 'red')
  const blueParticipants = getParticipantsByTeam(game.participants, 'blue')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link
          to="/games"
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Games
        </Link>
        <button
          onClick={handleDeleteGame}
          disabled={deleteGame.isPending}
          className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete Game
        </button>
      </div>

      {/* Game Info */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">
                {game.game_type === '1v1' ? '1v1' : '2v2'} Match
              </h1>
              <div className="flex items-center space-x-4 mt-2 text-blue-100">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span className="text-sm">{formatDate(game.date)}</span>
                </div>
                <div className="flex items-center">
                  <Target className="h-4 w-4 mr-1" />
                  <span className="text-sm">Target: {game.target_score}</span>
                </div>
              </div>
            </div>
            {game.quick_entry && (
              <div className="px-3 py-1 bg-white bg-opacity-20 rounded-full">
                <span className="text-sm font-medium">Quick Entry</span>
              </div>
            )}
          </div>
        </div>

        {/* Final Score */}
        <div className="px-6 py-8">
          <div className="flex items-center justify-center space-x-8">
            <div className={`text-center ${winner === 'red' ? 'opacity-100' : 'opacity-60'}`}>
              <div className="text-6xl font-bold text-red-600">{game.final_red_score}</div>
              <div className="text-lg font-medium text-gray-600 mt-2">Red Team</div>
              {winner === 'red' && <Trophy className="h-6 w-6 mx-auto mt-2 text-yellow-500" />}
            </div>
            
            <div className="text-4xl font-light text-gray-400">-</div>
            
            <div className={`text-center ${winner === 'blue' ? 'opacity-100' : 'opacity-60'}`}>
              <div className="text-6xl font-bold text-blue-600">{game.final_blue_score}</div>
              <div className="text-lg font-medium text-gray-600 mt-2">Blue Team</div>
              {winner === 'blue' && <Trophy className="h-6 w-6 mx-auto mt-2 text-yellow-500" />}
            </div>
          </div>
          
          {/* AI Summary */}
          {game.ai_summary && (
            <div className="px-6 py-4 mt-4 bg-gradient-to-r from-purple-50 to-pink-50 border-t-2 border-purple-200">
              <div className="flex items-center justify-between">
                <div className="flex-1 text-center">
                  <p className="text-xs font-medium text-purple-600 mb-1">💬 Selena AI</p>
                  <p className="text-lg font-bold text-gray-900 italic">{game.ai_summary}</p>
                </div>
                <button
                  onClick={handleRegenerateAI}
                  disabled={regenerateAI.isPending}
                  className="ml-4 px-3 py-2 text-sm font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded-lg transition-colors disabled:opacity-50 border border-purple-300"
                  title="Get a fresh take from Selena"
                >
                  {regenerateAI.isPending ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin inline mr-1" />
                      Asking...
                    </>
                  ) : (
                    <>Ask Selena</>
                  )}
                </button>
              </div>
            </div>
          )}
          
          {/* Generate AI Summary Button (if no summary exists) */}
          {!game.ai_summary && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button
                onClick={handleRegenerateAI}
                disabled={regenerateAI.isPending}
                className="w-full py-3 px-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {regenerateAI.isPending ? (
                  <>
                    <RefreshCw className="h-5 w-5 animate-spin" />
                    Asking Selena...
                  </>
                ) : (
                  <>
                    💬 What does Selena think?
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Participants */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Red Team */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 bg-red-50 border-b border-red-200">
            <h3 className="text-lg font-medium text-red-900 flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Red Team
            </h3>
          </div>
          <div className="px-6 py-4 space-y-3">
            {redParticipants.map((participant) => (
              <Link
                key={participant.id}
                to={`/players/${participant.user_id}`}
                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-200"
              >
                {participant.user.profile_picture_url ? (
                  <img
                    src={participant.user.profile_picture_url}
                    alt={participant.user.display_name}
                    className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-red-600" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{participant.user.display_name}</div>
                  <div className="text-sm text-gray-500">{getRoleLabel(participant.role)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Blue Team */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 bg-blue-50 border-b border-blue-200">
            <h3 className="text-lg font-medium text-blue-900 flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Blue Team
            </h3>
          </div>
          <div className="px-6 py-4 space-y-3">
            {blueParticipants.map((participant) => (
              <Link
                key={participant.id}
                to={`/players/${participant.user_id}`}
                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-200"
              >
                {participant.user.profile_picture_url ? (
                  <img
                    src={participant.user.profile_picture_url}
                    alt={participant.user.display_name}
                    className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{participant.user.display_name}</div>
                  <div className="text-sm text-gray-500">{getRoleLabel(participant.role)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Goals Timeline */}
      {game.goals && game.goals.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Zap className="h-5 w-5 mr-2" />
              Goals Timeline ({game.goals.length} goals)
            </h3>
          </div>
          <div className="px-6 py-4">
            <div className="space-y-4">
              {game.goals.map((goal: any, index: number) => {
                // Determine which team scored points (benefited) from this goal
                const benefitingTeam = getTeamThatScoredPoints(
                  goal.scored_by_team, 
                  goal.goal_type, 
                  goal.touched_by_defender,
                  goal.points_awarded
                )
                
                return (
                <div key={goal.id} className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                      benefitingTeam === 'red' ? 'bg-red-500' : 'bg-blue-500'
                    }`}>
                      {index + 1}
                    </div>
                  </div>
                  {goal.user?.profile_picture_url ? (
                    <img
                      src={goal.user.profile_picture_url}
                      alt={goal.user.display_name}
                      className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                    />
                  ) : goal.user ? (
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      benefitingTeam === 'red' ? 'bg-red-100' : 'bg-blue-100'
                    }`}>
                      <User className={`h-5 w-5 ${benefitingTeam === 'red' ? 'text-red-600' : 'text-blue-600'}`} />
                    </div>
                  ) : (
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      benefitingTeam === 'red' ? 'bg-red-100' : 'bg-blue-100'
                    }`}>
                      <User className={`h-5 w-5 ${benefitingTeam === 'red' ? 'text-red-600' : 'text-blue-600'}`} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div>
                        {goal.user ? (
                          <Link
                            to={`/players/${goal.user.id}`}
                            className="text-sm font-medium text-gray-900 hover:text-blue-600"
                          >
                            {goal.user.display_name}
                          </Link>
                        ) : (
                          <span className="text-sm font-medium text-gray-900">
                            {goal.scored_by_team === 'red' ? 'Red' : 'Blue'} {goal.shooter_role}
                          </span>
                        )}
                        <span className="text-sm text-gray-500"> • {getGoalTypeLabel(goal.goal_type)}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {goal.touched_by_defender && (
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            Touched
                          </span>
                        )}
                        <span className={`text-sm font-medium ${
                          goal.points_awarded > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {goal.points_awarded > 0 ? '+' : ''}{goal.points_awarded} pts
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center mt-1 text-xs text-gray-500">
                      <Clock className="h-3 w-3 mr-1" />
                      {formatTime(goal.goal_time, goal.created_at)}
                    </div>
                  </div>
                </div>
              )
              })}
            </div>
          </div>
        </div>
      )}

      {game.quick_entry && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            This game was recorded using Quick Entry mode. Goal details are not available.
          </p>
        </div>
      )}
    </div>
  )
}
